<script lang="ts" setup>
  import { ref, computed, watch, onMounted, useId, useTemplateRef, mergeProps } from 'vue';
  import { useFormContext, getModelFromContext, setModelWithContext } from '@othercommerce/foundation';

  interface Props {
    id?: string;
    name: string;
    label?: string;
    multiple?: boolean;
    disabled?: boolean;
  }

  defineOptions({ inheritAttrs: false });

  const props = withDefaults(defineProps<Props>(), {
    multiple: true,
  });

  const context = useFormContext();
  const random = useId();
  const fileInput = useTemplateRef('fileInput');

  const fileNames = ref<string[]>([]);

  const model = defineModel({
    get: (val: any) => getModelFromContext(props.name, context, val),
    set: (val: any) => setModelWithContext(props.name, context, val, fileInput.value),
  });

  const id = computed(() => props.id ?? random);

  function openPicker() {
    fileInput.value?.click();
  }

  function normalizeFilesFromEvent(target: HTMLInputElement | null) {
    if (!target) return null;
    const files = target.files;
    if (!files) return null;
    return props.multiple ? files : files?.[0];
  }

  function updateFileNamesFromModel(currentModel: any) {
    if (!currentModel) {
      fileNames.value = [];
      return;
    }
    if (currentModel instanceof FileList) {
      fileNames.value = Array.from(currentModel).map((f: File) => f.name);
    } else if (currentModel instanceof File) {
      fileNames.value = [currentModel.name];
    } else if (Array.isArray(currentModel)) {
      fileNames.value = currentModel.map((f: File) => f.name);
    } else {
      fileNames.value = [];
    }
  }

  function onFilesSelected(event: Event) {
    const target = event.target as HTMLInputElement | null;
    const filesOrFile = normalizeFilesFromEvent(target);

    model.value = filesOrFile;

    if (filesOrFile instanceof FileList) {
      fileNames.value = Array.from(filesOrFile).map(f => f.name);
    } else if (filesOrFile instanceof File) {
      fileNames.value = [filesOrFile.name];
    } else if (Array.isArray(filesOrFile)) {
      fileNames.value = filesOrFile.map((f: File) => f.name);
    } else {
      fileNames.value = [];
    }
  }

  onMounted(() => {
    updateFileNamesFromModel(model.value);
  });

  watch(() => model.value, (newVal) => {
    updateFileNamesFromModel(newVal);
  });
</script>

<template>
  <SfControlWrapper
    v-slot="{ errorId }"
    v-bind="{ id, name, label }"
  >
    <input
      :id="id"
      ref="fileInput"
      :aria-describedby="errorId"
      :multiple="multiple"
      :name="name"
      class="hidden"
      type="file"
      v-bind="mergeProps({ onChange: onFilesSelected }, $attrs)"
    />

    <div class="attachment">
      <ul v-if="fileNames.length" class="attachment__list">
        <li v-for="name in fileNames" class="attachment__item">{{ name }}</li>
      </ul>

      <SfButton
        :aria-label='`${label ?? $t("SHOP.COMMON.ARIA.ADD_ATTACHMENTS")}`'
        :disabled
        class="attachment__btn"
        primary
        square
        @click="openPicker"
      >
        <SfIconPlusCircle />
      </SfButton>
    </div>
  </SfControlWrapper>
</template>

<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-chat-attachment-list-color: var(--color-secondary, rgb(80, 80, 80));
  }


  .hidden {
    display: none;
  }

  .attachment {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    &__list {
      margin: 0;
      padding: 0;
      list-style: none;
      font-size: 0.75rem;
      color: var(--sf-chat-attachment-list-color);
    }

    &__item {
      margin: 0;
      padding: 0;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      max-width: 8rem;

      @include media.query(sm) {
        max-width: 4rem;
      }
    }

    &__btn {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
</style>
